<script lang="ts">
	import type { components } from '$api/schema';
	import type { HessianClient } from '$lib/client/hessianClient.svelte';
	import * as Dialog from '$lib/components/ui/dialog';
	import { getInitialsFromUsername } from '$lib/utils/initialsFromUsername';
	import { toast } from '$lib/utils/toast.svelte';
	import * as Avatar from './ui/avatar';
	import Button from './ui/button/button.svelte';
	import { Label } from './ui/label';

	type Organization = components['schemas']['Organization'];

	let {
		open = $bindable(false),
		organization,
		hessianClient,
		onUpdated
	}: {
		open?: boolean;
		organization: Organization;
		hessianClient: HessianClient;
		onUpdated?: () => void | Promise<void>;
	} = $props();

	// Supported types must match the server-side allow-list in
	// pkg/server/organizations.go::allowedLogoMIMETypes. Keep them in sync.
	const ACCEPTED_TYPES = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';
	// 5 MiB ceiling — generous for a logo, small enough that a fat-fingered
	// upload doesn't hang the dialog. Server side has its own implicit cap
	// via the presigned URL TTL, but we want fast client-side rejection.
	const MAX_BYTES = 5 * 1024 * 1024;

	let fileInput = $state<HTMLInputElement | null>(null);
	let selectedFile = $state<File | null>(null);
	let previewUrl = $state<string | null>(null);
	let isSaving = $state(false);

	const createUploadUrl = $derived(
		hessianClient.createMutation(
			'post',
			'/v1/organizations/{organizationId}/logo/upload-url'
		)
	);
	const updateOrganization = $derived(
		hessianClient.createMutation('patch', '/v1/organizations/{organizationId}')
	);

	const resetState = () => {
		selectedFile = null;
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			previewUrl = null;
		}
		if (fileInput) fileInput.value = '';
	};

	const closeDialog = () => {
		open = false;
		resetState();
	};

	const rejectFile = (message: string) => {
		// Drop any prior selection: otherwise Save would silently upload the
		// previously-valid file even though the user just tried to swap it
		// for an invalid one, and the preview stays showing the old image.
		// resetState() also clears the file input via the bind:this ref.
		resetState();
		toast.error(message);
	};

	const onFileChange = (event: Event) => {
		const target = event.currentTarget as HTMLInputElement;
		const file = target.files?.[0];
		if (!file) return;

		if (!ACCEPTED_TYPES.split(',').includes(file.type)) {
			rejectFile('Unsupported file type. Use PNG, JPEG, GIF, WebP, or SVG.');
			return;
		}
		if (file.size > MAX_BYTES) {
			rejectFile('File is too large. Maximum 5 MiB.');
			return;
		}

		selectedFile = file;
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		previewUrl = URL.createObjectURL(file);
	};

	const uploadAndSave = async () => {
		if (!selectedFile) return;

		isSaving = true;
		try {
			const { upload_url, storage_key } = await createUploadUrl.mutateAsync({
				params: { path: { organizationId: organization.id } },
				body: { content_type: selectedFile.type }
			});

			// Direct PUT to MinIO/S3. Must include the same Content-Type the
			// presigned URL was minted with — otherwise the signature check
			// will reject the upload.
			const putResponse = await fetch(upload_url, {
				method: 'PUT',
				headers: { 'Content-Type': selectedFile.type },
				body: selectedFile
			});

			if (!putResponse.ok) {
				const text = await putResponse.text().catch(() => '');
				throw new Error(`Upload failed (${putResponse.status}): ${text || putResponse.statusText}`);
			}

			await updateOrganization.mutateAsync({
				params: { path: { organizationId: organization.id } },
				body: { logo_storage_key: storage_key }
			});

			toast.success('Organization profile picture updated.');
			await onUpdated?.();
			closeDialog();
		} catch (err) {
			toast.error(
				`Failed to update profile picture: ${err instanceof Error ? err.message : String(err)}`
			);
		} finally {
			isSaving = false;
		}
	};

	const clearLogo = async () => {
		isSaving = true;
		try {
			await updateOrganization.mutateAsync({
				params: { path: { organizationId: organization.id } },
				body: { logo_storage_key: '' }
			});
			toast.success('Profile picture removed.');
			await onUpdated?.();
			closeDialog();
		} catch (err) {
			toast.error(
				`Failed to remove profile picture: ${err instanceof Error ? err.message : String(err)}`
			);
		} finally {
			isSaving = false;
		}
	};
</script>

<Dialog.Root
	bind:open
	onOpenChange={(o: boolean) => {
		if (!o) resetState();
	}}
>
	<Dialog.Content>
		<Dialog.Header>
			<Dialog.Title>Organization profile picture</Dialog.Title>
			<Dialog.Description>
				Upload a new picture for {organization.name}. Shown in the organization picker.
			</Dialog.Description>
		</Dialog.Header>

		<div class="flex flex-col gap-4 py-2">
			<div class="flex items-center gap-4">
				<Avatar.Root class="size-16">
					{#if previewUrl}
						<Avatar.Image src={previewUrl} alt="New organization logo preview" />
					{:else if organization.logo}
						<Avatar.Image src={organization.logo} alt="Current organization logo" />
					{/if}
					<Avatar.Fallback>{getInitialsFromUsername(organization.name)}</Avatar.Fallback>
				</Avatar.Root>

				<div class="flex flex-col gap-1">
					<Label for="org-logo-file">Picture</Label>
					<input
						bind:this={fileInput}
						id="org-logo-file"
						type="file"
						accept={ACCEPTED_TYPES}
						onchange={onFileChange}
						class="text-muted-foreground text-sm file:mr-3 file:rounded-md file:border file:bg-transparent file:px-3 file:py-1.5 file:text-sm file:font-medium"
					/>
					<p class="text-muted-foreground text-xs">PNG, JPEG, GIF, WebP, or SVG. Max 5 MiB.</p>
				</div>
			</div>
		</div>

		<Dialog.Footer class="gap-2">
			{#if organization.logo}
				<Button
					variant="ghost"
					onclick={clearLogo}
					disabled={isSaving}
					class="mr-auto text-destructive hover:text-destructive"
				>
					Remove picture
				</Button>
			{/if}
			<Button variant="outline" onclick={closeDialog} disabled={isSaving}>Cancel</Button>
			<Button onclick={uploadAndSave} disabled={!selectedFile || isSaving}>
				{isSaving ? 'Saving…' : 'Save'}
			</Button>
		</Dialog.Footer>
	</Dialog.Content>
</Dialog.Root>
